import {Dropdown, IconButton, Tooltip, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useColumnSortMode} from '../../../../store/selectors';
import {useEagGridStore} from '../../../../store/useEagGridStore';
import type {TransformedDefinitionApiResponse} from '../../../../types/Api';
import type {EagColumnState} from '../../../../types/EagColumnState';
import {getSortModel} from '../../../../utils/getSortModel';
import {SortDropdown} from '../../../SortDropdown/SortDropdown';
import {FilterCountBadge} from '../GridHeaderParts/GridHeaderParts';

type SortDropdownButtonProps = {
  columnStates: EagColumnState[];
  definition: TransformedDefinitionApiResponse;
} & TestIdProps;

export function SortDropdownButton(props: SortDropdownButtonProps) {
  const t = useTranslationContext();
  const store = useEagGridStore();
  const [isOpen, setIsOpen] = useState(false);

  const columnSortMode = useColumnSortMode();
  const activeSortCount = getSortModel(props.columnStates).length;

  if (columnSortMode === 'NONE') {
    return null;
  }

  return (
    <Dropdown
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement="bottom-end"
      isFocusManagementDisabled
      isKeyboardNavigationDisabled
      dropdownControl={
        <Tooltip label={t('page.datagrid.labels.sort')}>
          <span className="relative inline-flex">
            <IconButton
              aria-label={t('page.datagrid.labels.sort')}
              icon="content/sort"
              priority={isOpen ? 'secondary' : undefined}
              data-testid={suffixTestId('toggleSort', props)}
            />
            <Show when={activeSortCount > 0}>
              <FilterCountBadge
                count={activeSortCount}
                testId={suffixTestId('sortCountBadge', props)}
              />
            </Show>
          </span>
        </Tooltip>
      }
    >
      <SortDropdown
        columnStates={props.columnStates}
        definition={props.definition}
        onApply={(entries) => store.applyMultiSort(entries)}
        onClose={() => setIsOpen(false)}
        maxSorts={columnSortMode === 'SINGLE_SORT' ? 1 : undefined}
        data-testid={props['data-testid']}
      />
    </Dropdown>
  );
}
